import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { CgFilterSelect } from 'modules/Common/ui/CgFilterDropdown';
import { notifyError } from 'modules/Common/ui/toast/api';
import PlanStatusBadge from './PlanStatusBadge';
import { useAdminPlans } from '../hooks/useAdminPlans';
import {
  PRESETS,
  addPreset,
  expiryText,
  planNameText,
  priceForRequest,
  priceText,
} from '../utils/planDisplay';

/**
 * Change an organization's plan, its expiry and/or its price.
 *
 * There is deliberately NO status control: the API takes none (phase 2 §4).
 * "Force expire" is a button that sets the date to now and submits, and the button
 * label is derived from the picker rather than from a separate mode — so the UI and
 * the request can never disagree about what was meant. A status dropdown alongside
 * a date would allow exactly that disagreement.
 *
 * There is no currency control either: the currency is derived from the organization
 * server-side (D22) and only labels the price field.
 */

const toInputDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '');
const toInputPrice = (value) => (value === null || value === undefined ? '' : String(value));

export default function ManagePlanDialog({ organization, mutation, onClose }) {
  const current = organization.plan?.current ?? null;
  const currency = organization.plan?.currency ?? null;

  const plansQuery = useAdminPlans(organization.type);
  const planOptions = useMemo(
    () => (plansQuery.data?.plans ?? []).map((p) => ({ value: p._id, label: p.name })),
    [plansQuery.data],
  );

  // Defaults to the org's CURRENT plan, so a pure extension re-sends the same id
  // and changes nothing. The alternative — an empty dropdown — would make every
  // extension look like a plan change to whoever reads the request later.
  const [planId, setPlanId] = useState(current?._id ?? '');
  const [neverExpires, setNeverExpires] = useState(current?.to === null);
  const [expiresAt, setExpiresAt] = useState(toInputDate(current?.to));
  const [price, setPrice] = useState(toInputPrice(current?.price));

  // The picked date decides BOTH the verb and the timestamp sent, so the button
  // can never promise something different from what the request does.
  //
  // Compared at DAY granularity, and "today" counts as expiring. An earlier
  // version compared `endOf('day').isBefore(now)`, which made "Force expire now"
  // submit end-of-today — the plan stayed valid until midnight while the button
  // read "Extend". Both halves of that were wrong and only the label was visible.
  const chosen = expiresAt ? dayjs(expiresAt) : null;
  const isExpiring = !neverExpires && Boolean(chosen) && !chosen.isAfter(dayjs(), 'day');

  // "Save": the window is untouched, so the CURRENT `to` is re-sent verbatim and only the
  // plan or the price changes (D23). Without it a price edit read "Extend" and nudged `to`
  // to end-of-day. An already-lapsed plan whose date nobody touched is a Save too — else
  // editing its price would read "Force-expire" — but a plan lapsing LATER today is not:
  // force-expiring that one has to move `to` to now.
  const keepsWindow = neverExpires
    ? current?.to === null
    : Boolean(current?.to) && expiresAt === toInputDate(current.to);
  const lapsed = Boolean(current?.to) && !dayjs(current.to).isAfter(dayjs());
  const isSave = keepsWindow && (!isExpiring || lapsed);
  const verb = isSave ? 'Save' : isExpiring ? 'Force-expire' : 'Extend';

  // Empty is "not recorded" (null), which the backend keeps distinct from 0.
  const typedPrice = price.trim() === '' ? null : Number(price);
  const priceInvalid = typedPrice !== null && !(Number.isFinite(typedPrice) && typedPrice >= 0);

  const applyPreset = (delta) => {
    setNeverExpires(false);
    // Presets stack on the LATER of today and the current expiry, so extending a
    // plan with time left adds to it instead of silently shortening it.
    setExpiresAt(toInputDate(addPreset(current, delta)));
  };

  const forceExpire = () => {
    setNeverExpires(false);
    setExpiresAt(toInputDate(new Date()));
  };

  const handleSubmit = async () => {
    const priceField = priceForRequest({
      typed: typedPrice,
      currentPrice: current?.price,
      planChanged: Boolean(planId) && planId !== current?._id,
    });
    try {
      await mutation.mutateAsync({
        id: organization._id,
        payload: {
          // Omitted ⇒ keep the current plan and move only the window.
          ...(planId ? { planId } : {}),
          // REQUIRED and nullable server-side: an absent field is a 400, not a
          // default, so `null` is sent explicitly for never-expires (D8).
          //
          // An untouched window re-sends the stored `to` as-is. Otherwise a future
          // date grants the WHOLE of that day (end-of-day); today or earlier means
          // expire NOW, which is what "Force expire" has to do to be worth the name.
          expiresAt: neverExpires
            ? null
            : isSave
              ? current.to
              : isExpiring
                ? dayjs().toISOString()
                : chosen.endOf('day').toISOString(),
          // Sent only when omitting it would store the wrong price (D23).
          ...(priceField === undefined ? {} : { price: priceField }),
        },
      });
      onClose();
    } catch (err) {
      notifyError(err);
    }
  };

  const canSubmit = (neverExpires || Boolean(expiresAt)) && !priceInvalid;

  return (
    <CgDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Manage plan"
      description={organization.name}
      width={560}
      footer={
        <div className="flex justify-end gap-2">
          <CgRadixButton
            variant="secondary"
            label="Cancel"
            onClick={onClose}
          />
          <CgRadixButton
            variant={verb === 'Force-expire' ? 'danger' : 'primary'}
            label={verb}
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="plan-submit"
          />
        </div>
      }
    >
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900">
              {planNameText(organization.type, organization.plan)}
            </div>
            <div className="text-xs text-slate-500">
              Expires: {expiryText(organization.type, organization.plan)}
            </div>
            <div
              className="text-xs text-slate-500"
              data-testid="plan-current-price"
            >
              Price: {priceText(organization.plan)}
            </div>
          </div>
          <PlanStatusBadge plan={organization.plan} />
        </div>
      </div>

      <div className="mb-4">
        <label className="mb-1 block text-sm font-medium text-slate-700">Plan</label>
        <CgFilterSelect
          label={null}
          options={planOptions}
          value={planId || null}
          onChange={(v) => setPlanId(v ?? '')}
          placeholder={plansQuery.isFetching ? 'Loading plans…' : 'Keep current plan'}
        />
      </div>

      <div className="mb-4">
        <label
          className="mb-1 block text-sm font-medium text-slate-700"
          htmlFor="plan-price"
        >
          Price
        </label>
        <div className="flex items-center gap-2">
          <input
            id="plan-price"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={price}
            placeholder="Not recorded"
            onChange={(e) => setPrice(e.target.value)}
            aria-invalid={priceInvalid}
            data-testid="plan-price"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          {currency ? (
            <span
              className="shrink-0 text-sm font-medium text-slate-500"
              data-testid="plan-price-currency"
            >
              {currency}
            </span>
          ) : null}
        </div>
        <p className={`mt-1 text-xs ${priceInvalid ? 'text-red-600' : 'text-slate-500'}`}>
          {priceInvalid
            ? 'Enter a price of 0 or more.'
            : 'What this organization pays for the plan. Leave empty if it is not recorded — empty is not the same as 0.'}
        </p>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <CgRadixButton
            key={preset.label}
            variant="secondary"
            size="sm"
            label={preset.label}
            onClick={() => applyPreset(preset.delta)}
            data-testid={`plan-preset-${preset.label}`}
          />
        ))}
      </div>

      <div className="mb-3">
        <label
          className="mb-1 block text-sm font-medium text-slate-700"
          htmlFor="plan-expires-at"
        >
          Expires on
        </label>
        <input
          id="plan-expires-at"
          type="date"
          value={expiresAt}
          disabled={neverExpires}
          onChange={(e) => setExpiresAt(e.target.value)}
          data-testid="plan-expires-at"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
        />
      </div>

      <label className="mb-4 inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={neverExpires}
          onChange={(e) => setNeverExpires(e.target.checked)}
          data-testid="plan-never-expires"
          className="h-4 w-4 rounded border-slate-300 accent-primaryColor"
        />
        Never expires
      </label>

      <div>
        <CgRadixButton
          variant="secondary"
          label="Force expire now"
          onClick={forceExpire}
          data-testid="plan-force-expire"
        />
        <p className="mt-2 text-xs text-slate-500">
          Sets the expiry to today. Every user of this organization is blocked by the trial gate on
          their next request.
        </p>
      </div>
    </CgDialog>
  );
}
